import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { AccordionHeaderComponent } from '../accordion-header/accordion-header.component';
import { AccordionHeaderTemplateContext } from '../accordion-header/models/accordion-header-template-context.interface';
import { AccordionPanelComponent } from '../accordion-panel/accordion-panel.component';
import { AccordionPanelContentTemplateContext } from '../accordion-panel/models/accordion-panel-content-template-context.interface';
import { AccordionPanelHeaderTemplateContext } from '../accordion-panel/models/accordion-panel-header-template-context.interface';
import { AccordionPanelToggleEvent } from '../accordion-panel/models/accordion-panel-toggle-event.interface';
import { AccordionPanel } from '../accordion-panel/models/accordion-panel.interface';

@Component({
  selector: 'app-accordion',
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss',
  imports: [AccordionHeaderComponent, AccordionPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class AccordionComponent implements AfterContentInit {
  panels = input<AccordionPanel[]>();
  title = input<string | undefined>();
  hasBorder = input<boolean>(false);
  toggle = new EventEmitter<AccordionPanelToggleEvent>();

  @ContentChildren(AccordionPanelComponent)
  accordionPanels?: QueryList<AccordionPanelComponent>;

  @ContentChild('accordionHeaderTemplate', { static: true })
  accordionHeaderTemplate?: TemplateRef<AccordionHeaderTemplateContext>;

  @ContentChild('accordionPanelHeaderTemplate', { static: true })
  accordionPanelHeaderTemplate?: TemplateRef<AccordionPanelHeaderTemplateContext>;

  @ContentChild('accordionPanelContentTemplate', { static: true })
  accordionPanelContentTemplate?: TemplateRef<AccordionPanelContentTemplateContext>;

  readonly destroyRef: DestroyRef = inject(DestroyRef);

  ngAfterContentInit(): void {
    this.bindAccordionPanelToggle();
  }

  onToggle(event: AccordionPanelToggleEvent) {
    this.toggle.emit(event);
  }

  castToAccordionEvent(event: unknown): AccordionPanelToggleEvent {
    return event as AccordionPanelToggleEvent;
  }

  private bindAccordionPanelToggle(): void {
    this.accordionPanels
      ?.toArray()
      .forEach((accordionPanel: AccordionPanelComponent) => {
        accordionPanel.toggle
          .pipe(
            tap((event: AccordionPanelToggleEvent) => this.onToggle(event)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      });
  }
}
